/**
 * Payment Service
 * Frontend service for PayPal payment integration
 * Wraps Firebase callable functions and provides real-time status updates
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, Unsubscribe, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import {
  Checkout,
  CheckoutStatus,
  CreateOrderRequest,
  CreateOrderResponse,
  CaptureOrderRequest,
  CaptureOrderResponse,
  CreateCheckoutRequest,
  CreateCheckoutResponse,
  PaymentStatusUpdate,
  dollarsToCents,
} from '../types/payment';

const functions = getFunctions();

/**
 * Create a new checkout document in Firestore
 * This should be called before initiating PayPal payment
 */
export async function createCheckout(
  request: CreateCheckoutRequest
): Promise<CreateCheckoutResponse> {
  try {
    console.log('🔄 createCheckout called', {
      amount: request.amount,
      currency: request.currency,
      metadata: request.metadata,
      timestamp: new Date().toISOString()
    });

    // Generate unique checkout ID
    const checkoutId = `chk_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log('📝 Generated checkout ID:', checkoutId);
    
    const checkoutData: Omit<Checkout, 'id'> = {
      status: 'CREATED',
      amount: request.amount,
      currency: request.currency,
      paypal: {},
      metadata: request.metadata,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    // Create checkout document
    const checkoutDoc = {
      id: checkoutId,
      ...checkoutData,
    };
    
    console.log('💾 Saving checkout document to Firestore', {
      checkoutId,
      collection: 'checkouts',
      data: checkoutDoc
    });
    
    await setDoc(doc(db, 'checkouts', checkoutId), checkoutDoc);

    console.log('✅ Checkout created successfully', {
      checkoutId,
      status: 'CREATED',
      amount: request.amount
    });

    return {
      checkoutId,
      status: 'CREATED',
      amount: request.amount,
      currency: request.currency,
    };
  } catch (error: any) {
    console.error('❌ Error creating checkout', {
      error: error.message,
      code: error.code,
      name: error.name,
      stack: error.stack,
      fullError: error
    });
    throw new Error(`Failed to create checkout: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Get checkout details from Firestore
 */
export async function getCheckout(checkoutId: string): Promise<Checkout | null> {
  try {
    const checkoutDoc = await getDoc(doc(db, 'checkouts', checkoutId));
    
    if (!checkoutDoc.exists()) {
      return null;
    }

    return checkoutDoc.data() as Checkout;
  } catch (error) {
    console.error('Error getting checkout:', error);
    throw new Error('Failed to get checkout');
  }
}

/**
 * Create PayPal order via Cloud Function
 * This is called by the PayPal button's createOrder callback
 */
export async function createPayPalOrder(
  checkoutId: string
): Promise<CreateOrderResponse> {
  try {
    console.log('🔄 createPayPalOrder called', {
      checkoutId,
      functionName: 'createPayPalOrder',
      timestamp: new Date().toISOString()
    });

    const createOrder = httpsCallable<CreateOrderRequest, CreateOrderResponse>(
      functions,
      'createPayPalOrder'
    );

    console.log('📡 Calling Cloud Function: createPayPalOrder', { checkoutId });
    const result = await createOrder({ checkoutId });
    
    console.log('✅ PayPal order created via Cloud Function', {
      checkoutId,
      orderId: result.data.orderId,
      response: result.data
    });
    
    return result.data;
  } catch (error: any) {
    console.error('❌ Error creating PayPal order via Cloud Function', {
      checkoutId,
      error: error.message,
      code: error.code,
      details: error.details,
      stack: error.stack,
      fullError: error
    });
    
    // Extract error message from Firebase Functions error
    const message = error.message || 'Failed to create PayPal order';
    throw new Error(message);
  }
}

/**
 * Capture PayPal order via Cloud Function
 * This is called by the PayPal button's onApprove callback
 */
export async function capturePayPalOrder(
  checkoutId: string,
  orderId: string
): Promise<CaptureOrderResponse> {
  try {
    console.log('🔄 capturePayPalOrder called', {
      checkoutId,
      orderId,
      functionName: 'capturePayPalOrder',
      timestamp: new Date().toISOString()
    });

    const captureOrder = httpsCallable<CaptureOrderRequest, CaptureOrderResponse>(
      functions,
      'capturePayPalOrder'
    );

    console.log('📡 Calling Cloud Function: capturePayPalOrder', {
      checkoutId,
      orderId
    });
    const result = await captureOrder({ checkoutId, orderId });
    
    console.log('✅ PayPal order captured via Cloud Function', {
      checkoutId,
      orderId,
      captureId: result.data.captureId,
      response: result.data
    });
    
    return result.data;
  } catch (error: any) {
    console.error('❌ Error capturing PayPal order via Cloud Function', {
      checkoutId,
      orderId,
      error: error.message,
      code: error.code,
      details: error.details,
      stack: error.stack,
      fullError: error
    });
    
    // Extract error message from Firebase Functions error
    const message = error.message || 'Failed to capture PayPal order';
    throw new Error(message);
  }
}

/**
 * Subscribe to checkout status updates
 * Returns unsubscribe function
 * 
 * Usage:
 * const unsubscribe = subscribeToCheckoutStatus(checkoutId, (update) => {
 *   console.log('Status:', update.status);
 *   if (update.status === 'PAID') {
 *     // Payment confirmed!
 *   }
 * });
 * 
 * // Later: unsubscribe();
 */
export function subscribeToCheckoutStatus(
  checkoutId: string,
  onUpdate: (update: PaymentStatusUpdate) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  console.log('📡 subscribeToCheckoutStatus called', {
    checkoutId,
    collection: 'checkouts',
    timestamp: new Date().toISOString()
  });

  const checkoutRef = doc(db, 'checkouts', checkoutId);

  return onSnapshot(
    checkoutRef,
    (snapshot) => {
      console.log('📊 Firestore snapshot received', {
        checkoutId,
        exists: snapshot.exists(),
        timestamp: new Date().toISOString()
      });

      if (!snapshot.exists()) {
        console.warn('⚠️ Checkout document not found in Firestore', {
          checkoutId,
          collection: 'checkouts'
        });
        return;
      }

      const checkout = snapshot.data() as Checkout;
      
      const update: PaymentStatusUpdate = {
        checkoutId,
        status: checkout.status,
        timestamp: checkout.updatedAt,
        errorMessage: checkout.errorMessage,
      };

      console.log('📨 Checkout status update emitted', {
        checkoutId,
        status: checkout.status,
        previousStatus: snapshot.metadata.hasPendingWrites ? 'pending' : 'committed',
        hasErrorMessage: !!checkout.errorMessage,
        update
      });
      onUpdate(update);
    },
    (error) => {
      console.error('❌ Error in Firestore snapshot listener', {
        checkoutId,
        error: error.message,
        code: (error as any).code,
        stack: error.stack
      });
      if (onError) {
        onError(error);
      }
    }
  );
}

/**
 * Wait for checkout to reach PAID status
 * Returns a promise that resolves when payment is confirmed
 * Rejects if payment fails or times out
 * 
 * @param checkoutId - Checkout ID to monitor
 * @param timeoutMs - Timeout in milliseconds (default: 5 minutes)
 */
export function waitForPaymentConfirmation(
  checkoutId: string,
  timeoutMs: number = 300000 // 5 minutes
): Promise<Checkout> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      unsubscribe();
      reject(new Error('Payment confirmation timeout'));
    }, timeoutMs);

    const unsubscribe = subscribeToCheckoutStatus(
      checkoutId,
      (update) => {
        if (update.status === 'PAID') {
          clearTimeout(timeout);
          unsubscribe();
          
          // Fetch final checkout data
          getCheckout(checkoutId).then((checkout) => {
            if (checkout) {
              resolve(checkout);
            } else {
              reject(new Error('Checkout not found after payment'));
            }
          });
        } else if (update.status === 'FAILED' || update.status === 'CANCELED') {
          clearTimeout(timeout);
          unsubscribe();
          reject(new Error(update.errorMessage || 'Payment failed'));
        }
      },
      (error) => {
        clearTimeout(timeout);
        unsubscribe();
        reject(error);
      }
    );
  });
}

/**
 * Helper to create checkout from booking data
 * Converts booking information into checkout format
 */
export async function createCheckoutFromBooking(
  serviceType: string,
  sessionDuration: number,
  priceInDollars: number,
  customerEmail: string,
  customerName: string,
  bookingId?: string
): Promise<CreateCheckoutResponse> {
  const amountInCents = dollarsToCents(priceInDollars);

  const metadata: any = {
    customerEmail,
    customerName,
    serviceType,
    sessionDuration,
  };

  // Only add bookingId if it's provided (Firestore doesn't allow undefined)
  if (bookingId) {
    metadata.bookingId = bookingId;
  }

  const request: CreateCheckoutRequest = {
    amount: amountInCents,
    currency: 'USD',
    metadata,
  };

  return await createCheckout(request);
}

/**
 * Check if checkout is in a final state
 */
export function isCheckoutFinal(status: CheckoutStatus): boolean {
  return ['PAID', 'FAILED', 'CANCELED'].includes(status);
}

/**
 * Check if checkout can be retried
 */
export function canRetryCheckout(status: CheckoutStatus): boolean {
  return ['CREATED', 'FAILED'].includes(status);
}

/**
 * Get user-friendly status message
 */
export function getStatusMessage(status: CheckoutStatus): string {
  const messages: Record<CheckoutStatus, string> = {
    CREATED: 'Ready for payment',
    ORDER_CREATED: 'Processing payment...',
    CAPTURED: 'Payment captured, confirming...',
    PAID: 'Payment confirmed!',
    FAILED: 'Payment failed',
    CANCELED: 'Payment canceled',
  };

  return messages[status] || 'Unknown status';
}

/**
 * Get status color for UI
 */
export function getStatusColor(status: CheckoutStatus): string {
  const colors: Record<CheckoutStatus, string> = {
    CREATED: 'blue',
    ORDER_CREATED: 'yellow',
    CAPTURED: 'yellow',
    PAID: 'green',
    FAILED: 'red',
    CANCELED: 'gray',
  };

  return colors[status] || 'gray';
}
